import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Gift, Plane, ExternalLink, Copy, CheckCircle, 
  MapPin, Sparkles, Info, Globe, Building2, Users, Heart,
  Mail, Download, Star, CreditCard, Send, DollarSign
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Hotel Savings Card amounts
const HOTEL_CARD_AMOUNTS = [
  { value: 100, label: '$100', color: 'from-blue-500 to-cyan-500' },
  { value: 200, label: '$200', color: 'from-green-500 to-emerald-500' },
  { value: 300, label: '$300', color: 'from-purple-500 to-violet-500' },
  { value: 500, label: '$500', color: 'from-amber-500 to-orange-500' },
];

// Resort Stay Destinations with certificate links
const RESORT_DESTINATIONS = {
  'United States': [
    { name: 'Las Vegas, NV', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/16rj72', icon: '🎰' },
    { name: 'Denver, CO', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/ho4yqx', icon: '🏔️' },
    { name: 'Colorado Springs, CO', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/wbehpj', icon: '⛰️' },
    { name: 'Palm Springs, CA', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/1finzs', icon: '🌴' },
    { name: 'Lake Tahoe, CA/NV', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/t9l6y7', icon: '🏔️' },
    { name: 'Orlando, FL', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/8fcnl3', icon: '🎢' },
    { name: 'New York, NY', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/8yljfq', icon: '🗽' },
    { name: 'Chicago, IL', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/yhiq8d', icon: '🌆' },
    { name: 'San Diego, CA', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/eu72ym', icon: '🌊' },
  ],
  'Mexico': [
    { name: 'Cancun', duration: '6 Days / 5 Nights', link: 'https://members.marketingboost.com/rv_certificate/ngcau2', icon: '🏖️' },
    { name: 'Puerto Vallarta', duration: '6 Days / 5 Nights', link: 'https://members.marketingboost.com/rv_certificate/bjpywq', icon: '🌅' },
  ],
  'France': [
    { name: 'Paris', duration: '5 Days / 4 Nights', link: 'https://members.marketingboost.com/rv_certificate/8tq7k1', icon: '🗼' },
  ],
  'Brazil': [
    { name: 'Rio de Janeiro', duration: '5 Days / 4 Nights', link: 'https://members.marketingboost.com/rv_certificate/8tq7k1', icon: '🏖️' },
  ],
  'Canada': [
    { name: 'Toronto', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/l2exzg', icon: '🍁' },
    { name: 'Vancouver', duration: '4 Days / 3 Nights', link: 'https://members.marketingboost.com/rv_certificate/k7i5ya', icon: '🌲' },
  ],
  'Italy': [
    { name: 'Venice', duration: '5 Days / 4 Nights', link: 'https://members.marketingboost.com/rv_certificate/7n4zy5', icon: '🚣' },
  ],
};

const USE_CASES = [
  { icon: Users, title: 'Employee Rewards', desc: 'Recognize top performers with dream vacations' },
  { icon: Heart, title: 'Client Loyalty', desc: 'Thank clients for referrals or renewals' },
  { icon: Star, title: 'Lead Conversion', desc: 'Use as an irresistible offer to close deals' },
  { icon: Gift, title: 'Special Events', desc: 'Giveaways, contests, and celebrations' },
];

export default function GiftPortalPage() {
  const [selectedRegion, setSelectedRegion] = useState('United States');
  const [activeTab, setActiveTab] = useState('resort'); // 'resort' or 'hotel-card'
  const [copiedLink, setCopiedLink] = useState(null);
  const [generatedCount, setGeneratedCount] = useState(() => {
    const saved = localStorage.getItem('giftPortal_generatedCount');
    return saved ? parseInt(saved) : 0;
  });
  
  // Hotel Savings Card form
  const [hotelCardForm, setHotelCardForm] = useState({
    fullName: '',
    email: '',
    amount: 500,
    message: ''
  });
  const [sendingCard, setSendingCard] = useState(false);

  const handleGenerateCertificate = (destination) => {
    // Open the certificate link in a new tab
    window.open(destination.link, '_blank');
    
    // Track generation
    const newCount = generatedCount + 1;
    setGeneratedCount(newCount);
    localStorage.setItem('giftPortal_generatedCount', newCount.toString());
    
    toast.success(`Certificate generated for ${destination.name}!`, {
      description: 'Save the PDF and email it to your recipient.'
    });
  };

  const handleCopyLink = (destination) => {
    navigator.clipboard.writeText(destination.link);
    setCopiedLink(destination.name);
    setTimeout(() => setCopiedLink(null), 2000);
    toast.success('Link copied to clipboard!');
  };

  // Send Hotel Savings Card via Marketing Boost API
  const handleSendHotelCard = async () => {
    if (!hotelCardForm.fullName || !hotelCardForm.email) {
      toast.error('Please fill in recipient name and email');
      return;
    }
    
    setSendingCard(true);
    try {
      // Use Marketing Boost Hotel Savings Card API via script injection
      const script = document.createElement('script');
      script.src = 'https://members.marketingboost.com/jsapi/fbDE_Voucher.min.js';
      script.onload = () => {
        const fdDE_detail = {
          "fbDE_sender": '41064-42753',
          "fbDE_fullname": hotelCardForm.fullName,
          "fbDE_email": hotelCardForm.email,
          "fbDE_amount": hotelCardForm.amount,
          "fbDE_business": 48407,
          "fbDE_message": hotelCardForm.message || `Enjoy your $${hotelCardForm.amount} Hotel Savings Card from One Force Live!`
        };
        
        // Call the Marketing Boost API
        if (typeof window.fbDEVoucher === 'function') {
          window.fbDEVoucher(fdDE_detail);
          toast.success(`$${hotelCardForm.amount} Hotel Savings Card sent to ${hotelCardForm.email}!`, {
            description: 'The recipient will receive an email with their card.'
          });
          
          // Track and reset form
          const newCount = generatedCount + 1;
          setGeneratedCount(newCount);
          localStorage.setItem('giftPortal_generatedCount', newCount.toString());
          setHotelCardForm({ fullName: '', email: '', amount: 500, message: '' });
        } else {
          toast.error('Failed to load Hotel Card API. Please try again.');
        }
        setSendingCard(false);
      };
      script.onerror = () => {
        toast.error('Failed to connect to Hotel Card service');
        setSendingCard(false);
      };
      document.body.appendChild(script);
    } catch (error) {
      console.error('Error sending hotel card:', error);
      toast.error('Failed to send hotel card');
      setSendingCard(false);
    }
  };

  const regions = Object.keys(RESORT_DESTINATIONS);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-8 pt-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Gift className="w-7 h-7 text-amber-400" />
              Gift Portal
            </h1>
            <p className="text-gray-400 mt-1">
              Send resort stays & hotel savings cards to employees, clients & special events
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-white/5 rounded-lg px-4 py-2 border border-white/10">
              <p className="text-xs text-gray-500">Gifts Sent</p>
              <p className="text-xl font-bold text-amber-400">{generatedCount}</p>
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 p-1 bg-white/5 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('resort')}
            className={cn(
              "px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2",
              activeTab === 'resort'
                ? "bg-gradient-to-r from-amber-500 to-orange-500 text-black"
                : "text-gray-400 hover:text-white"
            )}
          >
            <Plane className="w-4 h-4" />
            Resort Stays
          </button>
          <button
            onClick={() => setActiveTab('hotel-card')}
            className={cn(
              "px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2",
              activeTab === 'hotel-card'
                ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-black"
                : "text-gray-400 hover:text-white"
            )}
          >
            <CreditCard className="w-4 h-4" />
            Hotel Savings Cards
          </button>
        </div>

        {/* Hotel Savings Card Section */}
        {activeTab === 'hotel-card' && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-cyan-500/20 rounded-lg">
                  <CreditCard className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-2">Hotel Savings Cards</h3>
                  <p className="text-gray-400 text-sm">
                    Send digital hotel savings cards worth <strong className="text-white">$100 - $500</strong> that recipients can use at thousands of hotels worldwide. 
                    Cards are delivered instantly via email!
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card Amount Selection */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {HOTEL_CARD_AMOUNTS.map((card) => (
                <motion.button
                  key={card.value}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={() => setHotelCardForm({ ...hotelCardForm, amount: card.value })}
                  className={cn(
                    "p-6 rounded-xl border-2 transition-all text-center",
                    hotelCardForm.amount === card.value
                      ? `bg-gradient-to-r ${card.color} border-transparent`
                      : "bg-white/5 border-white/10 hover:border-white/30"
                  )}
                >
                  <DollarSign className={cn(
                    "w-8 h-8 mx-auto mb-2",
                    hotelCardForm.amount === card.value ? "text-black" : "text-gray-400"
                  )} />
                  <p className={cn(
                    "text-2xl font-bold",
                    hotelCardForm.amount === card.value ? "text-black" : "text-white"
                  )}>
                    {card.label}
                  </p>
                  <p className={cn(
                    "text-xs mt-1",
                    hotelCardForm.amount === card.value ? "text-black/70" : "text-gray-500"
                  )}>
                    Hotel Savings
                  </p>
                </motion.button>
              ))}
            </div>

            {/* Send Card Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-cyan-400" />
                Send Hotel Savings Card
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Recipient Full Name *</label>
                  <Input
                    value={hotelCardForm.fullName}
                    onChange={(e) => setHotelCardForm({ ...hotelCardForm, fullName: e.target.value })}
                    placeholder="John Doe"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Recipient Email *</label>
                  <Input
                    type="email"
                    value={hotelCardForm.email}
                    onChange={(e) => setHotelCardForm({ ...hotelCardForm, email: e.target.value })}
                    placeholder="john@example.com"
                    className="input-dark"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm text-gray-400 mb-2">Personal Message (Optional)</label>
                <Input
                  value={hotelCardForm.message}
                  onChange={(e) => setHotelCardForm({ ...hotelCardForm, message: e.target.value })}
                  placeholder="Congratulations on your achievement! Enjoy this hotel savings card."
                  className="input-dark"
                />
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-400">
                  Sending: <span className="text-white font-bold">${hotelCardForm.amount}</span> Hotel Savings Card
                </div>
                <Button
                  onClick={handleSendHotelCard}
                  disabled={sendingCard || !hotelCardForm.fullName || !hotelCardForm.email}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-medium px-8"
                >
                  {sendingCard ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Card
                    </>
                  )}
                </Button>
              </div>
            </motion.div>

            {/* Hotel Card Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-green-500/10 border border-green-500/20 rounded-xl p-6"
            >
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                What Your Recipients Get
              </h3>
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  <Building2 className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                  <p className="text-2xl font-bold text-white mb-1">1,000,000+</p>
                  <p className="text-gray-400">Hotels and Resorts with top brands you trust!</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  <MapPin className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                  <p className="text-2xl font-bold text-white mb-1">8,000+</p>
                  <p className="text-gray-400">Car Rental locations nationwide with comparative pricing!</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-3" />
                  <p className="text-2xl font-bold text-white mb-1">Thousands</p>
                  <p className="text-gray-400">Activities and excursions to add to any vacation!</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                <p className="text-green-400 text-center font-medium">
                  <CheckCircle className="w-4 h-4 inline mr-2" />
                  No activation fee for your client to pay!
                </p>
              </div>
            </motion.div>

            {/* Partner Brands */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-xl p-6"
            >
              <h3 className="font-semibold text-white mb-4 text-center">Trusted Partner Brands</h3>
              <img 
                src="/images/hotel-card-brands.png" 
                alt="Partner hotel and travel brands" 
                className="w-full max-w-4xl mx-auto rounded-lg"
              />
            </motion.div>

            {/* FAQs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-xl p-6"
            >
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Info className="w-5 h-5 text-cyan-400" />
                Frequently Asked Questions
              </h3>
              <div className="space-y-4">
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-medium text-white mb-2">How does the Hotel Savings Card work?</h4>
                  <p className="text-gray-400 text-sm">Recipients receive an email with their digital savings card. They can use it to search and book hotels at discounted rates through our partner network of over 1 million hotels worldwide.</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-medium text-white mb-2">Is there an activation fee?</h4>
                  <p className="text-gray-400 text-sm">No! There is absolutely no activation fee. Your recipients can start using their savings card immediately at no cost.</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-medium text-white mb-2">How long is the card valid?</h4>
                  <p className="text-gray-400 text-sm">Hotel Savings Cards are valid for 18 months from the date of issue, giving recipients plenty of time to plan their perfect trip.</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-medium text-white mb-2">What can the savings be used for?</h4>
                  <p className="text-gray-400 text-sm">The savings apply to hotel room rates, car rentals, and vacation activities. Recipients can mix and match to create their ideal travel experience.</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-medium text-white mb-2">Can recipients book anywhere in the world?</h4>
                  <p className="text-gray-400 text-sm">Yes! Our network includes properties in destinations worldwide, from major cities to tropical resorts to mountain retreats.</p>
                </div>
              </div>
            </motion.div>

            {/* Terms */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/5 rounded-xl p-4 text-xs text-gray-500"
            >
              <h4 className="font-medium text-gray-400 mb-2">Terms of Use</h4>
              <ul className="space-y-1 list-disc list-inside">
                <li>Hotel Savings Cards are non-transferable after activation</li>
                <li>Savings apply to room rates and may vary by property and date</li>
                <li>Blackout dates may apply during peak travel periods</li>
                <li>Recipients must be 21+ years old to redeem</li>
                <li>Card value cannot be exchanged for cash</li>
                <li>Valid for 18 months from issue date</li>
              </ul>
            </motion.div>
          </>
        )}

        {/* Resort Stays Section */}
        {activeTab === 'resort' && (
          <>
        {/* Info Banner - How It Works */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-6"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-cyan-500/20 rounded-lg">
              <Info className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">How It Works</h3>
              <ol className="text-gray-400 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="bg-cyan-500/20 text-cyan-400 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                  <span>Select a destination and click <strong className="text-white">"Generate Certificate"</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-cyan-500/20 text-cyan-400 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                  <span>A new certificate will be created - <strong className="text-white">save the PDF</strong> or copy the redemption link</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-cyan-500/20 text-cyan-400 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                  <span><strong className="text-white">Email the certificate</strong> to your employee, client, or recipient</span>
                </li>
              </ol>
              <p className="text-amber-400 text-sm mt-3 font-medium">
                ⚡ Each click generates a unique certificate - unlimited use!
              </p>
            </div>
          </div>
        </motion.div>

        {/* Use Cases */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {USE_CASES.map((useCase, index) => (
            <motion.div
              key={useCase.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-amber-500/30 transition-colors"
            >
              <useCase.icon className="w-8 h-8 text-amber-400 mb-2" />
              <h4 className="font-medium text-white text-sm">{useCase.title}</h4>
              <p className="text-xs text-gray-500 mt-1">{useCase.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Region Tabs */}
        <div className="flex flex-wrap gap-2">
          {regions.map((region) => (
            <button
              key={region}
              onClick={() => setSelectedRegion(region)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                selectedRegion === region
                  ? "bg-gradient-to-r from-amber-500 to-orange-500 text-black"
                  : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
              )}
            >
              <Globe className="w-4 h-4 inline mr-2" />
              {region}
            </button>
          ))}
        </div>

        {/* Destinations Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {RESORT_DESTINATIONS[selectedRegion]?.map((destination, index) => (
            <motion.div
              key={destination.name}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-xl border border-white/10 overflow-hidden hover:border-amber-500/30 transition-all group"
            >
              {/* Destination Header */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className="text-3xl mb-2 block">{destination.icon}</span>
                    <h3 className="font-semibold text-white text-lg">{destination.name}</h3>
                    <p className="text-sm text-amber-400 font-medium">{destination.duration}</p>
                  </div>
                  <div className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs font-medium">
                    Active
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                  <Building2 className="w-3.5 h-3.5" />
                  <span>Premium Resort</span>
                  <span className="mx-1">•</span>
                  <Plane className="w-3.5 h-3.5" />
                  <span>Flights not included</span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleGenerateCertificate(destination)}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-medium"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Generate Certificate
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tips Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border border-amber-500/20 rounded-xl p-6"
        >
          <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Pro Tips for Maximum Impact
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-400">
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p><strong className="text-white">Email the PDF</strong> with a personalized message for a professional touch</p>
            </div>
            <div className="flex items-start gap-2">
              <Download className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p><strong className="text-white">Save certificates</strong> in a folder organized by recipient for tracking</p>
            </div>
            <div className="flex items-start gap-2">
              <Heart className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p><strong className="text-white">Use for referrals</strong> - "Refer 3 friends, get a Vegas vacation!"</p>
            </div>
            <div className="flex items-start gap-2">
              <Star className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p><strong className="text-white">Close deals faster</strong> - "Sign today and get a complimentary resort stay"</p>
            </div>
          </div>
        </motion.div>
        </>
        )}

        {/* Disclaimer */}
        {activeTab === 'resort' && (
          <p className="text-xs text-gray-600 text-center">
            Certificates are for resort accommodations only. Airfare, meals, and activities not included. 
            Recipients must pay taxes and resort fees at time of booking. Valid for 18 months from issue date.
          </p>
        )}
      </div>
    </DashboardLayout>
  );
}
